import * as React from "react"
import {
  PushAction,
  type WorkItem,
  WorkItemErrorType,
  WorkItemPushFieldType,
} from "@digits-graphql/frontend/graphql-bearer"
import {
  buildCategoryParamWorkItem,
  buildPartyParamWorkItem,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Actions/Shared"
import { useWorkItemPush } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/hooks/useWorkItemPush"
import { useWorkItemRowContext } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/WorkItemRowContext"
import { useStopClickPropagation } from "src/shared/hooks/useStopClickPropagation"

export function useAcceptField(workItem: WorkItem, field: WorkItemPushFieldType) {
  const { hasError, errorType } = useWorkItemPush(workItem, field)
  const { dispatch } = useWorkItemRowContext()

  const params = React.useMemo(() => {
    switch (field) {
      case WorkItemPushFieldType.Party:
        return buildPartyParamWorkItem(workItem, PushAction.Reassign, true)
      case WorkItemPushFieldType.Category:
        return buildCategoryParamWorkItem(workItem, true)
      default:
        return undefined
    }
  }, [workItem, field])

  const hideRetryButton = hasError && errorType === WorkItemErrorType.NotSupported

  const stopPropagation = useStopClickPropagation({ preventDefault: true })
  const onAcceptClick = React.useCallback(
    (event: React.MouseEvent) => {
      stopPropagation(event)

      if (params) {
        dispatch({ type: "PushField", field, params })
      }
    },
    [dispatch, field, params, stopPropagation]
  )

  const onIgnoreClick = React.useCallback(
    (event: React.MouseEvent) => {
      stopPropagation(event)
      dispatch({ type: "ResetField", field })
    },
    [dispatch, field, stopPropagation]
  )

  return React.useMemo(
    () => ({
      disabled: !params,
      hasError,
      hideRetryButton,
      onAcceptClick,
      onIgnoreClick,
    }),
    [params, hasError, hideRetryButton, onAcceptClick, onIgnoreClick]
  )
}
